import { Link } from "react-router-dom";
import { BookOpen, ArrowRight } from "lucide-react";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  image?: string;
}

const blogPosts: BlogPost[] = [
  {
    id: "3-mental-math-tricks",
    title: "3 Mental Math Tricks to Unlock Your Inner Superpower",
    excerpt:
      "Discover three powerful mental math tricks that will transform the way you approach calculations. Learn techniques that have been used for centuries to perform lightning-fast arithmetic.",
    date: "November 3, 2025",
    readTime: "5 min read",
    category: "Mental Math",
  },
  {
    id: "ithopu-how-it-helps-mental-math",
    title: "How Ithopu Helps Master Mental Math",
    excerpt:
      "Explore how Ithopu's innovative approach to teaching mental math combines ancient Vedic techniques with modern interactive learning to create a powerful learning experience.",
    date: "November 3, 2025",
    readTime: "6 min read",
    category: "Learning Platform",
  },
  {
    id: "ithopu-vedic-maths-tricks",
    title: "Vedic Maths Tricks: Ancient Wisdom for Modern Learning",
    excerpt:
      "Dive deep into the world of Vedic mathematics and discover how these ancient techniques can revolutionize your approach to problem-solving and mental calculation.",
    date: "November 3, 2025",
    readTime: "8 min read",
    category: "Vedic Math",
  },
];

export default function BlogList() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">
                Ithopu Blog
              </span>
            </Link>
            <Link
              to="/"
              className="text-gray-600 hover:text-blue-600 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Mental Math Insights</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Explore techniques, tips, and stories to help you master mental
            mathematics
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden"
            >
              {/* Category Badge */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                  <span className="text-xs text-gray-500">{post.readTime}</span>
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3 line-clamp-2">
                  {post.title}
                </h2>

                {/* Excerpt */}
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <span className="text-sm text-gray-500">{post.date}</span>
                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold group"
                  >
                    Read More
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Master Mental Math?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of students learning mental math techniques through
            interactive tutorials
          </p>
          <Link
            to="/register"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            Start Learning Free
          </Link>
        </div>
      </section>
    </div>
  );
}
